import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import { motion } from 'motion/react';
import { Package, ChevronRight } from 'lucide-react';

import { formatRupees, ORDER_STATUS_LABELS, type OrderStatus } from '@sreerasa/shared';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useMyOrders } from '@/lib/queries';
import { supabase } from '@/lib/supabase';
import { formatDateTime } from '@/lib/utils';
import { transitions } from '@/components/motion/primitives';

export const Route = createFileRoute('/orders/')({
  beforeLoad: async () => {
    const { data } = await supabase.auth.getSession();
    if (!data.session) throw redirect({ to: '/auth/sign-in', search: { redirect: '/orders' } });
  },
  component: OrdersPage,
});

export const STATUS_VARIANT: Record<
  OrderStatus,
  'neutral' | 'info' | 'warning' | 'success' | 'danger'
> = {
  pending_confirmation: 'warning',
  confirmed: 'info',
  packed: 'info',
  out_for_delivery: 'info',
  delivered: 'success',
  cancelled: 'danger',
  refunded: 'neutral',
};

function OrdersPage() {
  const { data: orders, isLoading } = useMyOrders();

  return (
    <div className="mx-auto max-w-4xl px-4 py-10 sm:px-6 lg:px-8">
      <h1 className="font-display text-display font-black text-soil-900">Your orders</h1>
      <p className="mt-2 text-soil-600">Everything you've bought, and where it's got to.</p>

      {isLoading ? (
        <div className="mt-8 space-y-3">
          {Array.from({ length: 3 }, (_, i) => (
            <Skeleton key={i} className="h-28 rounded-card" />
          ))}
        </div>
      ) : orders && orders.length > 0 ? (
        <div className="mt-8 space-y-3">
          {orders.map((order, index) => (
            <motion.div
              key={order.id}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ ...transitions.base, delay: index * 0.05 }}
            >
              <Link
                to="/orders/$id"
                params={{ id: order.id }}
                className="group flex items-center gap-4 rounded-card border border-soil-200 bg-white p-5 transition-all hover:-translate-y-0.5 hover:border-farm-300 hover:shadow-lift"
              >
                <div className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-farm-50 text-farm-600">
                  <Package className="size-5" aria-hidden />
                </div>

                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-display font-bold text-soil-900">
                      {order.order_number}
                    </span>
                    <Badge variant={STATUS_VARIANT[order.status]} size="sm">
                      {ORDER_STATUS_LABELS[order.status]}
                    </Badge>
                  </div>
                  <p className="mt-1 text-sm text-soil-500">
                    {formatDateTime(order.placed_at)} · {order.order_items?.length ?? 0}{' '}
                    {(order.order_items?.length ?? 0) === 1 ? 'item' : 'items'}
                  </p>
                </div>

                <div className="text-right">
                  <p className="font-display text-lg font-bold text-soil-900">
                    {formatRupees(order.total_paise)}
                  </p>
                  <p className="text-xs uppercase text-soil-400">{order.payment_method}</p>
                </div>

                <ChevronRight
                  className="size-5 shrink-0 text-soil-300 transition-transform group-hover:translate-x-1 group-hover:text-farm-600"
                  aria-hidden
                />
              </Link>
            </motion.div>
          ))}
        </div>
      ) : (
        <div className="mt-8 rounded-card border border-dashed border-soil-300 bg-white/50 p-16 text-center">
          <Package className="mx-auto size-9 text-soil-300" aria-hidden />
          <h2 className="mt-4 font-display text-xl font-bold text-soil-800">No orders yet</h2>
          <p className="mx-auto mt-2 max-w-sm text-sm text-soil-500">
            When you order, it'll show up here with live delivery status.
          </p>
          <Link to="/shop" className="mt-6 inline-block">
            <Button>Browse the shop</Button>
          </Link>
        </div>
      )}
    </div>
  );
}
